// Inquiry detail — thread view + status controls + admin reply composer.
// Reads/writes /admin/inquiries/:id.

import { useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { DetailLayout, DetailSection, DefRow } from '@drivecars/admin-ui';
import { EntityActivity } from '@/components/EntityActivity';
import { StatusPill } from '@drivecars/admin-ui';
import { Avatar } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  fetchAdminInquiry,
  replyToInquiry,
  setInquiryStatus,
  type AdminInquiryMessage,
  type InquiryStatus,
} from '@/lib/admin-api';

const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString() : '—';

function senderTone(sender: AdminInquiryMessage['sender']): string {
  if (sender === 'customer') return 'bg-[#eef2ff] border-[#c7d2fe] text-[#1e3a8a]';
  if (sender === 'vendor') return 'bg-[#ecfdf5] border-[#a7f3d0] text-[#065f46]';
  // admin
  return 'bg-[#fef3c7] border-[#fde68a] text-[#92400e]';
}

function senderLabel(sender: AdminInquiryMessage['sender']): string {
  if (sender === 'customer') return 'Customer';
  if (sender === 'vendor') return 'Vendor';
  return 'Admin';
}

export function InquiryDetail() {
  const params = useParams({ strict: false }) as { id?: string };
  const id = Number(params.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'inquiries', id],
    queryFn: ({ signal }) => fetchAdminInquiry(id, signal),
    enabled: Number.isFinite(id),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'inquiries'] });
  };

  const statusMut = useMutation({
    mutationFn: ({ next }: { next: InquiryStatus }) => setInquiryStatus(id, next),
    onSuccess: invalidate,
  });

  const replyMut = useMutation({
    mutationFn: (body: string) => replyToInquiry(id, body),
    onSuccess: () => {
      setDraft('');
      invalidate();
    },
  });

  if (!Number.isFinite(id)) {
    return (
      <DetailLayout title="Invalid id" backTo="/inquiries" backLabel="All inquiries">
        <div className="rounded-xl border border-[#fecaca] bg-[#fff1f2] p-5 text-[12.5px] text-[#9f1239]">
          Inquiry id must be numeric.
        </div>
      </DetailLayout>
    );
  }

  if (isLoading) {
    return (
      <DetailLayout title="Loading…" backTo="/inquiries" backLabel="All inquiries">
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 text-[12.5px] text-[color:var(--bo-mute)]">
          Loading inquiry…
        </div>
      </DetailLayout>
    );
  }
  if (error || !data) {
    return (
      <DetailLayout title="Not found" backTo="/inquiries" backLabel="All inquiries">
        <div className="rounded-xl border border-[#fecaca] bg-[#fff1f2] p-5 text-[12.5px] text-[#9f1239]">
          {error ? (error as Error).message : 'No inquiry with that id.'}
        </div>
      </DetailLayout>
    );
  }

  const inq = data;
  const mutationError = statusMut.error || replyMut.error;
  const busy = statusMut.isPending || replyMut.isPending;
  const trimmedDraft = draft.trim();

  return (
    <DetailLayout
      eyebrow="Trust · Inquiry"
      title={inq.subject}
      sub={`Opened ${fmtDate(inq.createdAt)} · last activity ${fmtDate(inq.updatedAt)}`}
      backTo="/inquiries"
      backLabel="All inquiries"
      actions={
        <>
          {inq.status !== 'replied' ? (
            <RowAction
              busy={busy}
              onClick={() => statusMut.mutate({ next: 'replied' })}
            >
              Mark replied
            </RowAction>
          ) : null}
          {inq.status !== 'closed' ? (
            <RowAction
              kind="danger"
              busy={busy}
              onClick={() => statusMut.mutate({ next: 'closed' })}
            >
              Close
            </RowAction>
          ) : null}
          {inq.status === 'closed' ? (
            <RowAction
              busy={busy}
              onClick={() => statusMut.mutate({ next: 'open' })}
            >
              Reopen
            </RowAction>
          ) : null}
          <StatusPill status={inq.status} />
        </>
      }
    >
      {mutationError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Mutation failed: {(mutationError as Error).message}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <DetailSection title="Customer">
          <div className="mb-3 flex items-center gap-3">
            <Avatar name={inq.customer.name} size={36} />
            <div className="min-w-0">
              <div className="truncate text-[14px] font-semibold">
                {inq.customer.name}
              </div>
              <div className="truncate text-[12px] text-[color:var(--bo-mute)]">
                {inq.customer.email}
              </div>
            </div>
          </div>
          <DefRow label="Contact email">{inq.contact.email}</DefRow>
          {inq.contact.phone ? (
            <DefRow label="Contact phone">{inq.contact.phone}</DefRow>
          ) : null}
          <button
            type="button"
            onClick={() =>
              void navigate({
                to: '/customers/$id' as never,
                params: { id: String(inq.customer.id) } as never,
              })
            }
            className="mt-3 text-[12px] font-medium text-[color:var(--bo-accent)] hover:underline"
          >
            View customer →
          </button>
        </DetailSection>

        <DetailSection title="Vendor">
          <div className="mb-3 flex items-center gap-3">
            <Avatar name={inq.vendor.name} size={36} />
            <div className="min-w-0">
              <div className="truncate text-[14px] font-semibold">
                {inq.vendor.name}
              </div>
              <div className="truncate text-[12px] text-[color:var(--bo-mute)]">
                {inq.vendor.email}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() =>
              void navigate({
                to: '/crew/$id' as never,
                params: { id: String(inq.vendor.id) } as never,
              })
            }
            className="mt-3 text-[12px] font-medium text-[color:var(--bo-accent)] hover:underline"
          >
            View vendor →
          </button>
        </DetailSection>

        <DetailSection title="Car">
          <DefRow label="Title">{inq.car.title ?? '—'}</DefRow>
          <DefRow label="Slug">
            <span className="font-mono text-[12px]">{inq.car.slug ?? '—'}</span>
          </DefRow>
          <button
            type="button"
            onClick={() =>
              void navigate({
                to: '/cars/$id' as never,
                params: { id: String(inq.car.id) } as never,
              })
            }
            className="mt-3 text-[12px] font-medium text-[color:var(--bo-accent)] hover:underline"
          >
            View car →
          </button>
        </DetailSection>
      </div>

      <DetailSection title={`Thread (${inq.messages.length})`}>
        {inq.messages.length === 0 ? (
          <div className="text-[12.5px] text-[color:var(--bo-mute)]">
            No messages yet.
          </div>
        ) : (
          <ol className="space-y-3">
            {inq.messages.map((m) => (
              <li
                key={m.id}
                className={`rounded-lg border px-4 py-3 ${senderTone(m.sender)}`}
              >
                <div className="mb-1 flex items-center justify-between gap-3 text-[11.5px]">
                  <span className="font-semibold uppercase tracking-[0.08em]">
                    {senderLabel(m.sender)}
                    {m.senderName ? (
                      <span className="ms-2 font-normal normal-case opacity-70">
                        · {m.senderName}
                      </span>
                    ) : null}
                  </span>
                  <span className="tabular-nums opacity-70">{fmtDate(m.sentAt)}</span>
                </div>
                <div className="whitespace-pre-wrap text-[13px] text-[color:var(--bo-ink)]">
                  {m.body}
                </div>
              </li>
            ))}
          </ol>
        )}
      </DetailSection>

      <DetailSection title="Reply as admin">
        <p className="mb-2 text-[11.5px] text-[color:var(--bo-mute)]">
          Admin replies are visible to customer and vendor in the thread but
          don&apos;t trigger an email — use this when intervening on an
          existing escalation.
        </p>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Write a message…"
          rows={4}
          className="block w-full resize-y rounded-lg border border-[color:var(--bo-line)] bg-white px-3 py-2 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)] focus:ring-2 focus:ring-[color:var(--bo-accent)]/30"
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-[11.5px] tabular-nums text-[color:var(--bo-mute)]">
            {trimmedDraft.length} / 10000
          </span>
          <RowAction
            kind="primary"
            busy={replyMut.isPending}
            disabled={!trimmedDraft || trimmedDraft.length > 10_000}
            onClick={() => replyMut.mutate(trimmedDraft)}
          >
            Send reply
          </RowAction>
        </div>
      </DetailSection>

      <EntityActivity entityType="inquiries" entityId={inq.id} title="Audit trail" />
    </DetailLayout>
  );
}
